import { ref, getDownloadURL, uploadBytes } from 'firebase/storage';
import { collection, addDoc, query, where, getDocs, getDoc, doc, setDoc } from 'firebase/firestore';
import { FIREBASE_STORAGE, FIREBASE_DB } from '../firebase.config.js';
import { FIREBASE_AUTH } from '../firebase.config.js';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

class W2ServiceClass {
  constructor() {
    this.storage = FIREBASE_STORAGE;
    this.db = FIREBASE_DB;
  }

  // Create watermarked version of PDF
  async createWatermarkedPDF(pdfBytes, employeeId) {
    try {
      // Load the PDF document
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const pages = pdfDoc.getPages();
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);

      // Add watermark to each page
      pages.forEach(page => {
        const { width, height } = page.getSize();
        
        // Add diagonal watermarks across the page
        for (let i = 0; i < 5; i++) {
          for (let j = 0; j < 5; j++) {
            const watermarkText = 'CONFIDENTIAL';
            const fontSize = 24;
            
            page.drawText(watermarkText, {
              x: (width / 4) * i,
              y: (height / 4) * j,
              size: fontSize,
              font: helveticaFont,
              color: rgb(0.85, 0.85, 0.85),
              opacity: 0.2,
              rotate: Math.PI / 4,
            });
          }
        }

        // Add employee ID and timestamp at the bottom
        const footerText = `Employee ID: ${employeeId} | Generated: ${new Date().toLocaleString()}`;
        page.drawText(footerText, {
          x: 30,
          y: 15,
          size: 10,
          font: helveticaFont,
          color: rgb(0.3, 0.3, 0.3),
        });

        // Add company name at the top
        page.drawText('SBG', {
          x: width - 50,
          y: height - 20,
          size: 12,
          font: helveticaFont,
          color: rgb(0.3, 0.3, 0.3),
        });
      });

      // Save the watermarked PDF
      const watermarkedPdfBytes = await pdfDoc.save();
      return new Uint8Array(watermarkedPdfBytes);
    } catch (error) {
      console.error('Error creating watermarked PDF:', error);
      throw new Error('Failed to create watermarked version');
    }
  }

  // Get W2 documents for a specific employee
  async getW2Documents(employeeId) {
    try {
      console.log('Fetching W2 documents for employee:', employeeId);
      
      if (!FIREBASE_AUTH.currentUser) {
        console.log('No authenticated user');
        throw new Error('User must be authenticated');
      }

      console.log('Current user:', {
        uid: FIREBASE_AUTH.currentUser.uid,
        emailVerified: FIREBASE_AUTH.currentUser.emailVerified
      });

      if (!FIREBASE_AUTH.currentUser.emailVerified) {
        console.log('Email not verified');
        throw new Error('Please verify your email before accessing W2 documents');
      }

      // Get user's role
      const userDoc = await getDoc(doc(this.db, 'users', FIREBASE_AUTH.currentUser.uid));
      console.log('User doc exists:', userDoc.exists());
      
      if (!userDoc.exists()) {
        console.log('User document not found');
        throw new Error('User not found');
      }

      const userData = userDoc.data();
      console.log('User data:', {
        role: userData.role,
        requestedEmployeeId: employeeId,
        currentUserUid: FIREBASE_AUTH.currentUser.uid
      });

      // Only allow access if user is the employee or an admin
      const isAdmin = userData.role === 'admin';
      if (FIREBASE_AUTH.currentUser.uid !== employeeId && !isAdmin) {
        console.log('Unauthorized access attempt');
        throw new Error('Unauthorized access');
      }

      // Query the w2Documents collection instead of storage
      const w2Query = query(
        collection(this.db, 'w2Documents'),
        where('employeeId', '==', employeeId)
      );
      
      console.log('Querying w2Documents collection');
      const querySnapshot = await getDocs(w2Query);
      console.log('Found documents:', querySnapshot.size);

      const documents = querySnapshot.docs.map(doc => ({
        id: doc.id,
        path: doc.id,
        name: doc.data().fileName,
        year: doc.data().year,
        uploadedAt: doc.data().uploadedAt
      }));

      console.log('Processed documents:', documents);
      return documents.sort((a, b) => b.year - a.year);
    } catch (error) {
      console.error('Error fetching W2 documents:', error);
      throw error;
    }
  }

  // Log document access
  async logDocumentAccess(employeeId, documentPath) {
    try {
      await addDoc(collection(this.db, 'w2AccessLogs'), {
        documentId: documentPath, // Required by security rules
        userId: FIREBASE_AUTH.currentUser.uid,
        accessedAt: new Date(),
        userAgent: navigator.userAgent,
        ipAddress: 'client', // Required by security rules
        accessType: 'download' // Must be either 'view' or 'download'
      });
    } catch (error) {
      console.error('Error logging document access:', error);
      // Don't throw the error as logging failure shouldn't prevent document access
    }
  }

  // Get temporary download URL for a W2 document
  async getDocumentUrl(employeeId, documentPath) {
    try {
      if (!FIREBASE_AUTH.currentUser) {
        throw new Error('User must be authenticated');
      }

      if (!FIREBASE_AUTH.currentUser.emailVerified) {
        throw new Error('Please verify your email before accessing W2 documents');
      }

      // Get user's role from Firestore
      const userDoc = await getDoc(doc(this.db, 'users', FIREBASE_AUTH.currentUser.uid));
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      // Only allow access if user is the employee or an admin
      const isAdmin = userDoc.data().role === 'admin';
      if (FIREBASE_AUTH.currentUser.uid !== employeeId && !isAdmin) {
        throw new Error('Unauthorized access');
      }

      // Get the document metadata first
      const w2Doc = await getDoc(doc(this.db, 'w2Documents', documentPath));
      if (!w2Doc.exists()) {
        throw new Error('Document not found');
      }

      // Verify the document belongs to the correct employee
      if (w2Doc.data().employeeId !== employeeId) {
        throw new Error('Invalid document access');
      }

      // Get the storage URL
      const storageRef = ref(this.storage, `w2Documents/${employeeId}/${w2Doc.data().fileName}`);
      const url = await getDownloadURL(storageRef);
      
      // Log the access (don't await, let it happen in background)
      this.logDocumentAccess(employeeId, documentPath).catch(console.error);

      return url;
    } catch (error) {
      console.error('Error getting document URL:', error);
      throw error;
    }
  }

  // Upload a new W2 document (admin only)
  async uploadW2Document(employeeId, year, file) {
    try {
      if (!FIREBASE_AUTH.currentUser) {
        throw new Error('User must be authenticated');
      }

      // Get current user's role from Firestore
      const userDoc = await getDoc(doc(this.db, 'users', FIREBASE_AUTH.currentUser.uid));
      if (!userDoc.exists() || userDoc.data().role !== 'admin') {
        throw new Error('Unauthorized: Only admins can upload W2 documents');
      }

      // Validate year
      const currentYear = new Date().getFullYear();
      if (year > currentYear) {
        throw new Error('Cannot upload W2s for future years');
      }

      // Validate file type
      if (file.type !== 'application/pdf') {
        throw new Error('Only PDF files are allowed');
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('File size must be less than 5MB');
      }

      const fileName = `w2_${year}.pdf`;
      const timestamp = new Date();
      
      // Upload original file to Storage first
      const storageRef = ref(this.storage, `w2Documents/${employeeId}/${fileName}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      try {
        // Create watermarked version
        const fileBuffer = await file.arrayBuffer();
        const watermarkedPdfBytes = await this.createWatermarkedPDF(fileBuffer, employeeId);
        
        // Upload watermarked version
        const previewRef = ref(this.storage, `w2Previews/${employeeId}/${fileName}`);
        await uploadBytes(previewRef, watermarkedPdfBytes, {
          contentType: 'application/pdf',
          customMetadata: {
            watermarked: 'true',
            originalPath: `w2Documents/${employeeId}/${fileName}`,
            timestamp: timestamp.toISOString()
          }
        });
        const previewURL = await getDownloadURL(previewRef);

        // Create document metadata in Firestore
        const docId = `${employeeId}_${year}`;
        await setDoc(doc(this.db, 'w2Documents', docId), {
          employeeId,
          year,
          uploadedAt: timestamp,
          fileName,
          fileUrl: downloadURL,
          previewUrl: previewURL,
          uploadedBy: FIREBASE_AUTH.currentUser.uid,
          contentType: file.type,
          size: file.size,
          hasPreview: true
        });

        return {
          downloadURL,
          previewURL,
          fileName,
          year
        };
      } catch (watermarkError) {
        console.error('Error creating preview:', watermarkError);
        
        // If preview creation fails, still save the original document
        const docId = `${employeeId}_${year}`;
        await setDoc(doc(this.db, 'w2Documents', docId), {
          employeeId,
          year,
          uploadedAt: timestamp,
          fileName,
          fileUrl: downloadURL,
          uploadedBy: FIREBASE_AUTH.currentUser.uid,
          contentType: file.type,
          size: file.size,
          hasPreview: false,
          previewError: watermarkError.message
        });

        return {
          downloadURL,
          fileName,
          year,
          previewError: 'Failed to create preview version'
        };
      }
    } catch (error) {
      console.error('Error uploading W2 document:', error);
      throw error;
    }
  }

  // Get W2 document preview URL
  async getPreviewUrl(employeeId, year) {
    try {
      const fileName = `w2_${year}.pdf`;
      const previewRef = ref(this.storage, `w2Previews/${employeeId}/${fileName}`);
      return await getDownloadURL(previewRef);
    } catch (error) {
      console.error('Error getting preview URL:', error);
      throw error;
    }
  }

  // Verify if a user has access to a specific W2 document
  async verifyAccess(userId, documentPath) {
    try {
      const employeeId = documentPath.split('/')[1]; // Extract employeeId from path
      return userId === employeeId; // Basic check - can be enhanced with additional rules
    } catch (error) {
      console.error('Error verifying access:', error);
      throw error;
    }
  }

  // Get access logs for a specific document or employee
  async getAccessLogs(employeeId) {
    try {
      const q = query(
        collection(this.db, 'w2AccessLogs'),
        where('employeeId', '==', employeeId)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching access logs:', error);
      throw error;
    }
  }
}

const W2Service = new W2ServiceClass();
export default W2Service; 